import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, Instagram, MessageCircle } from "lucide-react";
import { useSite, useCategories } from "@/lib/api";
import { NAV_LINKS, LOGO_URL } from "@/lib/site";
import { slugifyTr } from "@/lib/slugify";

export const Footer = () => {
  const { data: site } = useSite();
  const { data: categories = [] } = useCategories();

  const waUrl = site?.whatsapp
    ? `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(`Merhaba ${site.brand}, sipariş vermek istiyorum.`)}`
    : "#";

  return (
    <footer className="bg-charcoal text-cream">
      {/* slogan bandı */}
      <div className="border-b border-dashed border-cream/15">
        <div className="mx-auto max-w-7xl px-4 py-12 text-center sm:px-6 lg:px-8">
          <p className="font-serif text-4xl italic text-cream sm:text-6xl">İyi Et Tesadüf Değildir</p>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-2.5 rounded-sm bg-[#25D366] px-7 py-3.5 text-base font-bold uppercase tracking-wide text-charcoal transition-colors hover:bg-[#1eb858]"
          >
            <MessageCircle className="h-5 w-5" /> WhatsApp ile Sipariş Ver
          </a>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-cream">
                <img src={LOGO_URL} alt="Ertan Kasap" className="h-12 w-12 object-contain" />
              </span>
              <div>
                <span className="block font-serif text-xl text-cream">Ertan Kasap</span>
                <span className="block font-mono text-[9px] uppercase tracking-[0.3em] text-copper">Est. 1985</span>
              </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-cream/60">{site?.sub_slogan}</p>
          </div>

          <div>
            <h4 className="mb-4 font-mono text-[11px] uppercase tracking-[0.2em] text-cream/40">Keşfet</h4>
            <ul className="space-y-2.5">
              {NAV_LINKS.slice(1).map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-cream/70 hover:text-copper transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>

            {categories.length > 0 && (
              <>
                <h4 className="mb-3 mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-cream/40">Kategoriler</h4>
                <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {categories.map((c) => (
                    <li key={c.slug || c.name}>
                      <Link
                        to={`/kategori/${slugifyTr(c.name)}`}
                        data-testid={`footer-category-${slugifyTr(c.name)}`}
                        className="text-sm text-cream/70 hover:text-copper transition-colors"
                      >
                        {c.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div>
            <h4 className="mb-4 font-mono text-[11px] uppercase tracking-[0.2em] text-cream/40">İletişim</h4>
            <ul className="space-y-3 text-sm text-cream/70">
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-copper" />
                <span>{site?.address}</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 flex-shrink-0 text-copper" />
                <a href={`tel:${site?.phone_link}`} className="hover:text-cream">{site?.phone}</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Instagram className="h-4 w-4 flex-shrink-0 text-copper" />
                <a href={site?.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-cream">{site?.instagram_handle}</a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="mb-4 font-mono text-[11px] uppercase tracking-[0.2em] text-cream/40">Çalışma Saatleri</h4>
            <ul className="space-y-2.5">
              {(site?.working_hours || []).map((wh) => (
                <li key={wh.day} className="flex items-start gap-2.5 text-sm text-cream/70">
                  <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-copper" />
                  <span>
                    <span className="block text-cream/90">{wh.day}</span>
                    <span className="font-mono text-xs text-cream/50">{wh.hours}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-dashed border-cream/15 pt-6 text-center font-mono text-[11px] uppercase tracking-wider text-cream/40">
          © {new Date().getFullYear()} Ertan Kasap Gıda Ürünleri Tic. San. Ltd. Şti. · 1985’ten beri ustanın elinden
        </div>
      </div>
    </footer>
  );
};

export default Footer;
